import React from "react"
import { Box, Typography } from "@mui/material"
import { AppPillToggle } from "@components"
import { dsColorNew } from '@palette'
import { batch, useSignal } from "@preact/signals-react"
import { dsRepricingDrawerData } from "Pages/DsRepricing/Page/@signals"
import DsRepricingSeasons from "Pages/DsRepricing/Page/@components/SeasonDates"
import DsRepricingLeadTimes from "Pages/DsRepricing/Page/@components/LeadTimes"
import DsRepricingWeekdays from "Pages/DsRepricing/Page/@components/Weekdays"
import DsRepricingPickupDates from "Pages/DsRepricing/Page/@components/PickupDates"
import {
	PERIOD_MODE, PERIOD_MODE_FIELD, periodMode, readPeriod,
} from "Pages/DsRepricing/Page/@components/periodShape"

// Two ways of answering "when does this rule apply": a calendar range or a distance to the pickup.
// They are mutually exclusive — mixing them in one rule was ambiguous — so the mode picks one and
// clears the other.
//
// Weekdays used to be a third mode and is now a filter on top of whichever mode is active (Curro,
// 2026-09-19). The old shape could not express "the weekends of July": you picked the month or the
// day, and a rule that needed both was two rules. It survives a mode switch, and empty means every
// day.
//
// There is no `periodMode` field in the payload: a saved rule carries seasons or leadTimes and never
// both, so the data already says which mode it is and there is no second source of truth to drift.
export default function DsRepricingPeriod() {
	const mode = useSignal(periodMode(dsRepricingDrawerData.peek()))
	const is = value => mode.value === value

	const handleMode = (_, value) => {
		// Re-picking the active mode does nothing; keeping the current mode is what stops the rule
		// from ending up with no period at all.
		if (!value || value === mode.value) return
		batch(() => {
			mode.value = value
			// Everything the other two modes own is emptied, so the period can only ever carry one.
			const period = readPeriod(dsRepricingDrawerData.value)
			const cleared = Object.entries(PERIOD_MODE_FIELD)
				.filter(([key]) => key !== value)
				.reduce((acc, [, field]) => ({ ...acc, [field]: [] }), period)
			dsRepricingDrawerData.value = { ...dsRepricingDrawerData.value, period: cleared }
		})
	}

	return (
		<Box>
			<Typography sx={{ fontSize: 14, color: dsColorNew.textDark, fontWeight: 600 }}>Apply by</Typography>
			<Typography component="span" sx={{ fontSize: 12, color: dsColorNew.textMuted, display: 'block', mb: 1.25 }}>
				A calendar period or a distance to the pickup — one of the two. Narrow it by weekday below.
			</Typography>

			<AppPillToggle size="small" value={mode.value} onChange={value => handleMode(null, value)} sx={{ mb: 2 }}
			               options={[
				               { value: PERIOD_MODE.SEASON, label: 'Season dates' },
				               { value: PERIOD_MODE.LEAD_TIME, label: 'Lead time' },
			               ]}/>

			{/* Every block stays mounted so a hidden one keeps publishing a valid state instead of
			    leaving a stale error that would block Next on a field nobody can see. */}
			<Box sx={{ display: is(PERIOD_MODE.SEASON) ? 'block' : 'none' }}>
				<DsRepricingSeasons active={is(PERIOD_MODE.SEASON)}/>
			</Box>
			<Box sx={{ display: is(PERIOD_MODE.LEAD_TIME) ? 'block' : 'none' }}>
				<DsRepricingLeadTimes active={is(PERIOD_MODE.LEAD_TIME)}/>
			</Box>
			{/* Always on: it narrows either mode instead of replacing one. */}
			<Box sx={{ mt: 2.5 }}>
				<DsRepricingWeekdays/>
			</Box>
			{/* The pickup dates searched, and the pickup / dropoff time of the rentals (CU-869fkr1a9). */}
			<Box sx={{ mt: 2.5 }}>
				<DsRepricingPickupDates/>
			</Box>
		</Box>
	)
}